import * as React from "react";
import { useState } from "react";
import {
  Button,
  Field,
  Input,
  InputOnChangeData,
  Textarea,
  TextareaOnChangeData,
  tokens,
  makeStyles,
  Checkbox,
  Popover,
  Tooltip,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { getDocumentText, type InvalidOccurrence } from "../taskpane";
import { useApi } from "../../api/useApi";
import { CheckTextStandardsRequest, DocumentsIsoClient, StandardsIsoClient } from "../../api/IsoApiServer";
import InvalidOccurrenceItem from "./InvalidOccurenceItem";
import { Settings24Regular } from "@fluentui/react-icons";


/* global HTMLTextAreaElement */

interface TextInsertionProps {
  insertText: (text: string) => Promise<void>;
  // validateDocument: () => Promise<InvalidOccurrence[]>;
  highlightInvalidOccurrence: (occurrence: InvalidOccurrence) => Promise<void>;
  replaceInvalidOccurrence: (occurrence: InvalidOccurrence, replacementText: string, replaceAll: boolean) => Promise<void>;
}

const useStyles = makeStyles({
  instructions: {
    fontWeight: tokens.fontWeightSemibold,
    marginTop: "20px",
    marginBottom: "10px",
  },
  textPromptAndInsertion: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  textAreaField: {
    marginLeft: "20px",
    marginTop: "30px",
    marginBottom: "20px",
    marginRight: "20px",
    maxWidth: "50%",
  },
  validateButton: {
    marginTop: "12px",
    backgroundColor: '#1976D2',
    color: '#FFFFFF', // Set foreground color to white
    ':hover': {
      backgroundColor: '#155A9C', // Darker shade of the main color on hover
      color: '#FFFFFF', // Keep the text color white on hover
    },
    ':disabled': {
      backgroundColor: '#D3D3D3', // Light gray background when disabled
      color: '#A9A9A9', // Darker gray foreground when disabled
      cursor: 'not-allowed', // Show not-allowed cursor when disabled
    },
  },
  invalidList: {
    width: "100%",
    maxWidth: "700px",
    marginTop: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "0px",
  },
  invalidItem: {
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  invalidContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  invalidToken: {
    fontWeight: tokens.fontWeightSemibold,
  },
  invalidActions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  replacementField: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  replacementInput: {
    minWidth: "200px",
  },
  settingsButton: {
    marginTop: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  settingsPopover: {
    padding: "16px",
    maxWidth: "300px",
  },
});


const TextInsertion: React.FC<TextInsertionProps> = (props: TextInsertionProps) => {
  const [text, setText] = useState<string>("Some text.");
  const [invalidOccurrences, setInvalidOccurrences] = useState<InvalidOccurrence[]>([]);
  const [hasValidated, setHasValidated] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replacementText, setReplacementText] = useState<string>("");
  const [isApplyingChange, setIsApplyingChange] = useState<boolean>(false);
  const { authorizedApi } = useApi(DocumentsIsoClient);
  const { authorizedApi: standardsApi } = useApi(StandardsIsoClient);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<string[]>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const stored = localStorage.getItem("selectedCategories");
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(() => {
    const stored = localStorage.getItem("selectedDate");
    return stored ? stored : null;
  });

  React.useEffect(() => {
    if (!authorizedApi) return;
    standardsApi.categories().then((res) => {
      setCategories(res.categories ?? []);
    });
  }, [authorizedApi]);

  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) => {
      const isSz = category === 'SŽ';
      let newSelection: string[];

      if (isSz) {
        // SŽ is mutually exclusive with other categories
        newSelection = prev.includes('SŽ') ? [] : ['SŽ'];
      } else {
        // Non-SŽ categories: remove SŽ if present, toggle the selected category
        const withoutSz = prev.filter((c) => c !== 'SŽ');
        newSelection = withoutSz.includes(category)
          ? withoutSz.filter((c) => c !== category)
          : [...withoutSz, category];
      }

      localStorage.setItem("selectedCategories", JSON.stringify(newSelection));
      return newSelection;
    });
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    localStorage.setItem("selectedDate", newDate);
  };

  const handleValidateDocument = async () => {
    setIsValidating(true);
    try {
      const wordText = await getDocumentText();
      const result = await authorizedApi.checkTextStandards(new CheckTextStandardsRequest({
        text: wordText,
        selectedCategories: selectedCategories,
        selectedDate: new Date(selectedDate ?? Date.now()),
      }));
      const occurrences: InvalidOccurrence[] = [];

      // Sort by position to ensure consistent ordinal assignment
      const sortedStandards = [...result.invalidStandards].sort(
        (a, b) => (a.wordStartPosition ?? 0) - (b.wordStartPosition ?? 0)
      );

      // Track ordinals per search text (case-insensitive) for Word document search indexing
      const ordinalCounters: Record<string, number> = {};

      sortedStandards.forEach((isoStandard) => {
        // Use the actual matched text (paragraphText) for Word search operations
        // This handles case/spacing variations like "ED.2" vs "ed. 2"
        const searchText = isoStandard.paragraphText || isoStandard.identificator;
        const ordinalKey = searchText.toLowerCase();
        const ordinal = ordinalCounters[ordinalKey] ?? 0;
        ordinalCounters[ordinalKey] = ordinal + 1;

        occurrences.push({
          id: `${isoStandard.identificator}-${isoStandard.wordStartPosition ?? ordinal}`,
          text: searchText,
          label: isoStandard.identificator + ' ' + isoStandard.name,
          message: `Nalezen chybný standard "${isoStandard.identificator}".`,
          ordinal,
          position: isoStandard.wordStartPosition ?? 0,
          validToString: isoStandard.validToString,
          anotation: isoStandard.anotation,
          description: isoStandard.description,
          detailUrl: isoStandard.detailUrl,
          validStandards: isoStandard.validStandards,
        });
      });
      setInvalidOccurrences(occurrences);
      setHasValidated(true);
      setEditingId(null);
      setReplacementText("");
    } catch (error) {
      console.log("Validation error:", error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleHighlight = async (occurrence: InvalidOccurrence) => {
    await props.highlightInvalidOccurrence(occurrence);
  };

  const handleStartEdit = (occurrence: InvalidOccurrence) => {
    setEditingId(occurrence.id);
    setReplacementText(occurrence.text);
  };

  const handleReplacementChange = (_event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    setReplacementText(data.value);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setReplacementText("");
  };

  const handleSaveReplacement = async (current: InvalidOccurrence, replaceAll = false) => {
    if (!current.id) {
      return;
    }

    const target = invalidOccurrences.find((item) => item.id === current.id);
    if (!target) {
      return;
    }

    setIsApplyingChange(true);
    try {
      setInvalidOccurrences((prevOccurrences) => {
        if (replaceAll) {
          // remove all occurrences that match the same token/text
          return prevOccurrences.filter((item) => item.text !== target.text);
        }

        // single replacement: remove only the replaced occurrence and adjust ordinals after it
        const updated = prevOccurrences
          .filter((item) => item.id !== current.id)
          .map((item) => {
            if (item.text === target.text && item.ordinal > target.ordinal) {
              return { ...item, ordinal: item.ordinal - 1 };
            }
            return item;
          });
        return updated;
      });
    } catch (error) {
      console.log("Replacement error:", error);
    } finally {
      setIsApplyingChange(false);
    }
  };

  const styles = useStyles();

  return (
    <div className={styles.textPromptAndInsertion}>
      {/* <Field className={styles.textAreaField} size="large" label="Enter text to be inserted into the document.">
        <Textarea size="large" value={text} onChange={handleTextChange} />
      </Field>
      <Field className={styles.instructions}>Click the button to insert text.</Field>
      <Button appearance="primary" size="large" onClick={handleTextInsertion}>
        Insert text
      </Button> */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', width: '90%', paddingRight: '20px', paddingLeft: '20px' }}>
        <Button
          appearance="secondary"
          size="large"
          className={styles.validateButton}
          onClick={handleValidateDocument}
          disabled={isValidating || isApplyingChange}
        >
          {isValidating ? "Ověřuji…" : "Ověřit platnost předpisů"}
        </Button>

        <Popover trapFocus>
          <PopoverTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              size="large"
              className={styles.settingsButton}
              icon={<Settings24Regular />}
            >
              Nastavení
            </Button>
          </PopoverTrigger>
          <PopoverSurface className={styles.settingsPopover}>

            <Field label="Datum platnosti">
              <Input
                type="date"
                value={selectedDate || ""}
                onChange={handleDateChange}
              />
            </Field>
            <Field label="Kategorie">
              {categories?.map((category) =>
                <Checkbox
                  label={category}
                  checked={selectedCategories.includes(category)}
                  onChange={() => handleCategoryChange(category)}
                />)}


            </Field>
          </PopoverSurface>
        </Popover>
      </div>
      {hasValidated && invalidOccurrences.length === 0 && (
        <Field className={styles.instructions}>Žádné neplatné normy nenalezeny.</Field>
      )}
      {invalidOccurrences.length > 0 && (
        <div className={styles.invalidList}>
          {invalidOccurrences.map((occurrence, index) => (
            <InvalidOccurrenceItem
              key={occurrence.id} // Adjust key as needed
              occurrence={occurrence}
              index={index}
              selected={false} // Manage selection state if needed
              onClick={(data) => { props.highlightInvalidOccurrence(data); }}
              onReplaceChange={(data, replaceAll, newText) => { props.replaceInvalidOccurrence(data, newText, replaceAll); handleSaveReplacement(occurrence, replaceAll); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TextInsertion;